// ============================================
// Admin Dashboard - Fixed version with proper JSON handling
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// ============================================
// Type Definitions
// ============================================

interface Persona {
  id: string;
  name: string;
  name_ar?: string;
  role: string;
  description?: string;
  prompt?: string;
  safety_level: number;
  is_system: number;
  is_active: number;
  personality_traits?: string | string[];
  expertise?: string | string[];
  color?: string;
}

interface PersonaVersion {
  id: string;
  version: number;
  name: string;
  prompt: string;
  safety_level: number;
  created_at: number;
  created_by?: string;
}

interface AdminStats {
  total_sessions?: number;
  total_children?: number;
  total_parents?: number;
  total_turns?: number;
  total_input_tokens?: number;
  total_output_tokens?: number;
}

const API_BASE = 'https://equantu-kids-api.doziva228.workers.dev/api';

// ============================================
// Safe JSON Parse Helper
// ============================================

function safeJsonParse<T>(jsonStr: string | null | undefined, defaultValue: T): T {
  if (!jsonStr || jsonStr === 'null' || jsonStr === 'undefined') {
    return defaultValue;
  }
  try {
    return JSON.parse(jsonStr);
  } catch {
    return defaultValue;
  }
}

function safeGetPersonaField(field: string | string[] | undefined, defaultValue: string = ''): string {
  if (!field) return defaultValue;
  if (Array.isArray(field)) return field.join(', ');
  if (typeof field === 'string') {
    try {
      const parsed = JSON.parse(field);
      return Array.isArray(parsed) ? parsed.join(', ') : String(parsed);
    } catch {
      return field;
    }
  }
  return defaultValue;
}

// ============================================
// Safety Settings Panel Component
// ============================================

function SafetySettingsPanel() {
  const [settings, setSettings] = useState({
    maxDailySessions: 100,
    maxSessionDuration: 30,
    maxDailyMinutes: 120,
    blockedCategories: ['violence', 'sexual', 'terrorism', 'drugs', 'selfHarm'],
    redirectTopics: ['politics', 'sectarian'],
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const getAdminToken = () => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = localStorage.getItem('equantu-auth');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Zustand persist stores state directly, not under .state
        return parsed.adminToken || null;
      }
    } catch {
      return null;
    }
    return null;
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      const token = getAdminToken();
      const response = await fetch(`${API_BASE}/admin/safety-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          defaultLimits: {
            maxDailySessions: settings.maxDailySessions,
            maxSessionDuration: settings.maxSessionDuration,
            maxDailyMinutes: settings.maxDailyMinutes,
          },
          blockedCategories: settings.blockedCategories,
          redirectTopics: settings.redirectTopics,
        }),
      });

      if (response.ok) {
        setMessage('✅ 设置保存成功！');
      } else {
        const error = await response.json();
        setMessage(`❌ 保存失败: ${error.error || '未知错误'}`);
      }
    } catch (error: any) {
      setMessage(`❌ 保存失败: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="text-xl font-bold text-gray-800 mb-4">默认限制设置</h2>
      <p className="text-gray-600 mb-6">新注册用户的默认限制值</p>

      {message && (
        <div className={`mb-4 p-3 rounded-lg ${message.includes('✅') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-gray-600 mb-1">每日会话次数</label>
          <input 
            type="number" 
            value={settings.maxDailySessions}
            onChange={(e) => setSettings({...settings, maxDailySessions: parseInt(e.target.value)})}
            className="w-full px-3 py-2 border rounded"
          />
          <span className="text-sm text-gray-500">次</span>
        </div>
        <div>
          <label className="block text-gray-600 mb-1">单次会话时长</label>
          <input 
            type="number" 
            value={settings.maxSessionDuration}
            onChange={(e) => setSettings({...settings, maxSessionDuration: parseInt(e.target.value)})}
            className="w-full px-3 py-2 border rounded"
          />
          <span className="text-sm text-gray-500">分钟</span>
        </div>
        <div>
          <label className="block text-gray-600 mb-1">每日总时长</label>
          <input 
            type="number" 
            value={settings.maxDailyMinutes}
            onChange={(e) => setSettings({...settings, maxDailyMinutes: parseInt(e.target.value)})}
            className="w-full px-3 py-2 border rounded"
          />
          <span className="text-sm text-gray-500">分钟</span>
        </div>
      </div>

      <div className="bg-yellow-50 p-4 rounded-lg mb-6">
        <p className="text-sm text-yellow-800">
          💡 <strong>提示：</strong>这些是新注册用户的默认值。现有用户的设置不会受影响。
        </p>
      </div>

      <button 
        onClick={handleSave}
        disabled={loading}
        className="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
      >
        {loading ? '保存中...' : '保存设置'}
      </button>
    </div>
  );
}

// ============================================
// Admin Dashboard Component
// ============================================

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'personas' | 'safety' | 'history' | 'stats'>('personas');
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);
  const [versions, setVersions] = useState<PersonaVersion[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  // 表单状态
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPersona, setEditingPersona] = useState<Persona | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    role: '',
    description: '',
    prompt: '',
    safety_level: 3,
    personality_traits: '',
    expertise: '',
    color: '#6366F1',
  });

  const getAdminToken = () => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = localStorage.getItem('equantu-auth');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Zustand persist stores state directly, not under .state
        return parsed.adminToken || null;
      }
    } catch {
      return null;
    }
    return null;
  };
  const adminToken = getAdminToken();

  // Safe fetch helper
  const safeFetch = useCallback(async (url: string, options?: RequestInit) => {
    if (!adminToken) {
      throw new Error('No authentication token');
    }
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminToken}`,
        ...options?.headers,
      },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    return response.json();
  }, [adminToken]);

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    fetchPersonas();
    fetchStats();
  }, [adminToken]);

  const fetchPersonas = async () => {
    try {
      const data = await safeFetch(`${API_BASE}/admin/personas`);
      setPersonas(data.personas || []);
    } catch (err) {
      console.error('Failed to fetch personas:', err);
      setError('加载人设列表失败');
    }
  };

  const fetchVersions = async (personaId: string) => {
    try {
      const data = await safeFetch(`${API_BASE}/admin/personas/${personaId}/versions`);
      setVersions(data.versions || []);
    } catch (err) {
      console.error('Failed to fetch versions:', err);
      setError('加载版本历史失败');
    }
  };

  const fetchStats = async () => {
    try {
      const data = await safeFetch(`${API_BASE}/admin/stats`);
      setStats(data.stats || {});
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      // Stats might not be available, don't show error
    }
  };

  const handleCreatePersona = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await safeFetch(`${API_BASE}/admin/personas`, {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          personality_traits: formData.personality_traits.split(',').map(s => s.trim()).filter(Boolean),
          expertise: formData.expertise.split(',').map(s => s.trim()).filter(Boolean),
        }),
      });
      
      if (res.message || res.persona) {
        setMessage('人设创建成功！');
        setShowCreateModal(false);
        setFormData({
          name: '',
          name_ar: '',
          role: '',
          description: '',
          prompt: '',
          safety_level: 3,
          personality_traits: '',
          expertise: '',
          color: '#6366F1',
        });
        fetchPersonas();
      } else {
        setError('创建失败：服务器返回无效响应');
      }
    } catch (err) {
      console.error('Failed to create persona:', err);
      setError('创建失败: ' + (err as Error).message);
    }
    setLoading(false);
  };

  const handleUpdatePersona = async () => {
    if (!editingPersona) return;
    setLoading(true);
    setError('');
    try {
      const res = await safeFetch(`${API_BASE}/admin/personas/${editingPersona.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          prompt: formData.prompt,
          safety_level: formData.safety_level,
          description: formData.description,
          personality_traits: formData.personality_traits.split(',').map(s => s.trim()).filter(Boolean),
          expertise: formData.expertise.split(',').map(s => s.trim()).filter(Boolean),
        }),
      });
      
      if (res.message || res.persona) {
        setMessage('人设更新成功！');
        setEditingPersona(null);
        fetchPersonas();
      } else {
        setError('更新失败：服务器返回无效响应');
      }
    } catch (err) {
      console.error('Failed to update persona:', err);
      setError('更新失败: ' + (err as Error).message);
    }
    setLoading(false);
  };

  const handleRollback = async (personaId: string, version: number) => {
    if (!confirm(`确定要回滚到版本 ${version} 吗？当前版本将被保存为新历史。`)) return;
    
    setLoading(true);
    setError('');
    try {
      const res = await safeFetch(
        `${API_BASE}/admin/personas/${personaId}/versions/${version}/rollback`,
        { method: 'POST' }
      );
      
      if (res.message) {
        setMessage(`已成功回滚到版本 ${version}`);
        fetchPersonas();
        fetchVersions(personaId);
      } else {
        setError('回滚失败：服务器返回无效响应');
      }
    } catch (err) {
      console.error('Failed to rollback:', err);
      setError('回滚失败: ' + (err as Error).message);
    }
    setLoading(false);
  };

  const handleDeletePersona = async (id: string) => {
    if (!confirm('确定要删除这个人设吗？此操作不可恢复。')) return;
    
    setLoading(true);
    setError('');
    try {
      const res = await safeFetch(`${API_BASE}/admin/personas/${id}`, {
        method: 'DELETE',
      });
      
      if (res.message) {
        setMessage('人设已删除');
        fetchPersonas();
      } else {
        const data = await res.json();
        setError(data.error || '删除失败');
      }
    } catch (err) {
      console.error('Failed to delete persona:', err);
      setError('删除失败: ' + (err as Error).message);
    }
    setLoading(false);
  };

  const openEditModal = (persona: Persona) => {
    setEditingPersona(persona);
    setFormData({
      name: persona.name,
      name_ar: persona.name_ar || '',
      role: persona.role,
      description: persona.description || '',
      prompt: persona.prompt || '',
      safety_level: persona.safety_level,
      personality_traits: safeGetPersonaField(persona.personality_traits, ''),
      expertise: safeGetPersonaField(persona.expertise, ''),
      color: persona.color || '#6366F1',
    });
  };

  const safetyLevelColors: Record<number, string> = {
    1: 'bg-red-100 text-red-700',
    2: 'bg-orange-100 text-orange-700',
    3: 'bg-yellow-100 text-yellow-700',
    4: 'bg-green-100 text-green-700',
    5: 'bg-blue-100 text-blue-700',
  };

  const safetyLevelNames: Record<number, string> = {
    1: '严格',
    2: '很严格',
    3: '适中',
    4: '宽松',
    5: '最宽松',
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Equantu Kids AI</h1>
            <p className="text-sm text-gray-500">厂商管理后台 v2.0</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">管理员</span>
            <button
              onClick={() => {
                if (typeof window !== 'undefined') {
                  localStorage.removeItem('equantu-auth');
                }
                navigate('/admin/login');
              }}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
            >
              退出
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-6">
        {/* Sidebar */}
        <aside className="w-64 shrink-0">
          <nav className="bg-white rounded-xl shadow-sm p-4 space-y-2">
            <button
              onClick={() => setActiveTab('personas')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'personas' ? 'bg-blue-50 text-blue-600 font-medium' : 'hover:bg-gray-50'
              }`}
            >
              🎭 人设管理
            </button>
            <button
              onClick={() => setActiveTab('safety')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'safety' ? 'bg-blue-50 text-blue-600 font-medium' : 'hover:bg-gray-50'
              }`}
            >
              🛡️ 安全设置
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'history' ? 'bg-blue-50 text-blue-600 font-medium' : 'hover:bg-gray-50'
              }`}
            >
              📜 对话历史
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'stats' ? 'bg-blue-50 text-blue-600 font-medium' : 'hover:bg-gray-50'
              }`}
            >
              📊 数据统计
            </button>
          </nav>

          {/* Quick Stats */}
          {stats && (
            <div className="bg-white rounded-xl shadow-sm p-4 mt-4">
              <h3 className="font-semibold text-gray-700 mb-3">概览</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">总会话</span>
                  <span className="font-medium">{stats.total_sessions || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">儿童数</span>
                  <span className="font-medium">{stats.total_children || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">家长数</span>
                  <span className="font-medium">{stats.total_parents || 0}</span>
                </div>
              </div>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="flex-1">
          {error && (
            <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError('')}>✕</button>
            </div>
          )}

          {message && (
            <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg flex items-center justify-between">
              <span>{message}</span>
              <button onClick={() => setMessage('')}>✕</button>
            </div>
          )}

          {/* Personas Tab */}
          {activeTab === 'personas' && (
            <div className="space-y-6">
              {/* Toolbar */}
              <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-800">AI 人设管理</h2>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  ➕ 新建人设
                </button>
              </div>

              {/* Personas Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {personas.map((persona) => (
                  <div key={persona.id} className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xl font-bold"
                          style={{ backgroundColor: persona.color || '#6366F1' }}
                        >
                          {persona.name.charAt(0)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{persona.name}</h3>
                          <p className="text-sm text-gray-500">{persona.role}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${safetyLevelColors[persona.safety_level] || 'bg-gray-100'}`}>
                          {safetyLevelNames[persona.safety_level] || `安全${persona.safety_level}`}
                        </span>
                        {persona.is_system === 1 && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                            系统
                          </span>
                        )}
                      </div>
                    </div>

                    {persona.description && (
                      <p className="text-sm text-gray-600 mb-3">{persona.description}</p>
                    )}

                    <div className="flex items-center gap-2 mb-4">
                      <span className={`px-2 py-1 rounded text-xs ${persona.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {persona.is_active ? '启用' : '禁用'}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(persona)}
                        className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                      >
                        编辑
                      </button>
                      <button
                        onClick={() => {
                          setSelectedPersona(persona);
                          fetchVersions(persona.id);
                        }}
                        className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                      >
                        历史版本
                      </button>
                      {persona.is_system !== 1 && (
                        <button
                          onClick={() => handleDeletePersona(persona.id)}
                          className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                        >
                          删除
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Safety Tab */}
          {activeTab === 'safety' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">5档安全边界配置</h2>
              <p className="text-gray-600 mb-6">全局安全过滤规则，所有家长设置的基础</p>

              {/* Safety Levels Explanation */}
              <div className="grid grid-cols-5 gap-4 mb-6">
                {[1, 2, 3, 4, 5].map((level) => (
                  <div key={level} className={`rounded-lg p-4 ${safetyLevelColors[level]}`}>
                    <h4 className="font-bold text-center mb-1">等级 {level}</h4>
                    <p className="text-xs text-center">{safetyLevelNames[level]}</p>
                  </div>
                ))}
              </div>

              {/* Default Safety Rules */}
              <div className="space-y-6">
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-3">🔒 绝对禁止内容</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" defaultChecked className="w-4 h-4" />
                      <span>暴力内容</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" defaultChecked className="w-4 h-4" />
                      <span>色情内容</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" defaultChecked className="w-4 h-4" />
                      <span>恐怖主义</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" defaultChecked className="w-4 h-4" />
                      <span>毒品相关</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" defaultChecked className="w-4 h-4" />
                      <span>自残/自杀</span>
                    </label>
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-3">⚠️ 敏感话题处理</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" defaultChecked className="w-4 h-4" />
                      <span>政治话题 → 重定向</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" defaultChecked className="w-4 h-4" />
                      <span>教派分歧 → 重定向</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" className="w-4 h-4" />
                      <span>死亡话题 → 重定向</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" className="w-4 h-4" />
                      <span>恐惧内容 → 重定向</span>
                    </label>
                  </div>
                </div>

                <SafetySettingsPanel />
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">对话历史管理</h2>
                <div className="flex gap-2">
                  <input type="date" className="px-3 py-2 border rounded-lg" />
                  <input type="date" className="px-3 py-2 border rounded-lg" />
                  <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                    筛选
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">会话ID</th>
                      <th className="px-4 py-3 text-left">儿童</th>
                      <th className="px-4 py-3 text-left">年龄</th>
                      <th className="px-4 py-3 text-left">角色</th>
                      <th className="px-4 py-3 text-left">开始时间</th>
                      <th className="px-4 py-3 text-left">轮数</th>
                      <th className="px-4 py-3 text-left">Tokens</th>
                      <th className="px-4 py-3 text-left">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-500 font-mono text-xs">abc123...</td>
                      <td className="px-4 py-3">小明</td>
                      <td className="px-4 py-3">6-8岁</td>
                      <td className="px-4 py-3">Noor老师</td>
                      <td className="px-4 py-3">2024-01-15 10:30</td>
                      <td className="px-4 py-3">12</td>
                      <td className="px-4 py-3">2,450</td>
                      <td className="px-4 py-3">
                        <button className="text-blue-600 hover:underline">查看</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                <span>显示 1-20 条，共 156 条</span>
                <div className="flex gap-2">
                  <button className="px-3 py-1 border rounded hover:bg-gray-50">上一页</button>
                  <button className="px-3 py-1 border rounded hover:bg-gray-50">下一页</button>
                </div>
              </div>
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === 'stats' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-800">数据统计</h2>

              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <p className="text-sm text-gray-500 mb-1">总会话数</p>
                    <p className="text-3xl font-bold text-blue-600">{stats.total_sessions || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <p className="text-sm text-gray-500 mb-1">注册儿童</p>
                    <p className="text-3xl font-bold text-green-600">{stats.total_children || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <p className="text-sm text-gray-500 mb-1">注册家长</p>
                    <p className="text-3xl font-bold text-purple-600">{stats.total_parents || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <p className="text-sm text-gray-500 mb-1">总对话轮数</p>
                    <p className="text-3xl font-bold text-orange-600">{stats.total_turns || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <p className="text-sm text-gray-500 mb-1">输入 Tokens</p>
                    <p className="text-3xl font-bold text-gray-700">{stats.total_input_tokens || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <p className="text-sm text-gray-500 mb-1">输出 Tokens</p>
                    <p className="text-3xl font-bold text-gray-700">{stats.total_output_tokens || 0}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Create Persona Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold">新建人设</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">名称 *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="如: 新角色名称"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">阿拉伯语名称</label>
                  <input
                    type="text"
                    value={formData.name_ar}
                    onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="如: نور"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">角色定位 *</label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="如: 活泼的历史老师"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                  placeholder="简短描述这个角色的特点"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">安全级别</label>
                <select
                  value={formData.safety_level}
                  onChange={(e) => setFormData({ ...formData, safety_level: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value={1}>等级 1 - 严格</option>
                  <option value={2}>等级 2 - 很严格</option>
                  <option value={3}>等级 3 - 适中</option>
                  <option value={4}>等级 4 - 宽松</option>
                  <option value={5}>等级 5 - 最宽松</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">System Prompt *</label>
                <textarea
                  value={formData.prompt}
                  onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                  rows={8}
                  placeholder="输入详细的 system prompt..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">性格特点 (逗号分隔)</label>
                <input
                  type="text"
                  value={formData.personality_traits}
                  onChange={(e) => setFormData({ ...formData, personality_traits: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="如: 温柔, 耐心, 善于鼓励"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">专长领域 (逗号分隔)</label>
                <input
                  type="text"
                  value={formData.expertise}
                  onChange={(e) => setFormData({ ...formData, expertise: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="如: 古兰经教学, 故事讲述"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">主题色</label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-full h-10 px-3 py-1 border rounded-lg"
                />
              </div>
            </div>
            <div className="p-6 border-t flex gap-3 justify-end">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleCreatePersona}
                disabled={loading || !formData.name || !formData.role || !formData.prompt}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? '创建中...' : '创建'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Persona Modal */}
      {editingPersona && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold">编辑人设: {editingPersona.name}</h2>
              <button onClick={() => setEditingPersona(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">安全级别</label>
                <select
                  value={formData.safety_level}
                  onChange={(e) => setFormData({ ...formData, safety_level: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value={1}>等级 1 - 严格</option>
                  <option value={2}>等级 2 - 很严格</option>
                  <option value={3}>等级 3 - 适中</option>
                  <option value={4}>等级 4 - 宽松</option>
                  <option value={5}>等级 5 - 最宽松</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">System Prompt</label>
                <textarea
                  value={formData.prompt}
                  onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                  rows={8}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">性格特点</label>
                <input
                  type="text"
                  value={formData.personality_traits}
                  onChange={(e) => setFormData({ ...formData, personality_traits: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">专长领域</label>
                <input
                  type="text"
                  value={formData.expertise}
                  onChange={(e) => setFormData({ ...formData, expertise: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <div className="p-6 border-t flex gap-3 justify-end">
              <button
                onClick={() => setEditingPersona(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleUpdatePersona}
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      {selectedPersona && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">版本历史: {selectedPersona.name}</h2>
                <p className="text-sm text-gray-500">当前版本: {selectedPersona.safety_level}</p>
              </div>
              <button onClick={() => setSelectedPersona(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {versions.length === 0 ? (
                <p className="text-center text-gray-500 py-8">暂无版本历史</p>
              ) : (
                <div className="space-y-3">
                  {versions.map((version) => (
                    <div key={version.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">版本 {version.version}</span>
                          <span className={`px-2 py-1 rounded-full text-xs ${safetyLevelColors[version.safety_level]}`}>
                            {safetyLevelNames[version.safety_level] || `安全${version.safety_level}`}
                          </span>
                        </div>
                        <span className="text-sm text-gray-500">
                          {new Date(version.created_at * 1000).toLocaleString('zh-CN')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2 mb-2">{version.prompt?.substring(0, 200)}...</p>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            alert(`版本 ${version.version}:\n\n${version.prompt?.substring(0, 500) || '无内容'}`);
                          }}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
                        >
                          查看
                        </button>
                        <button
                          onClick={() => handleRollback(selectedPersona.id, version.version)}
                          disabled={loading}
                          className="px-3 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 text-sm disabled:opacity-50"
                        >
                          {loading ? '处理中...' : '回滚'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
